import { create } from 'zustand';
import type { Features } from '@/api/features';

interface FeaturesState {
	/** Флаг без значения ещё не известен: серверные флаги появляются с ответом /v1/features */
	flags: Partial<Record<string, boolean>>;
	isEnabled: (flag: string) => boolean;
	setFlag: (flag: string, value: boolean) => void;
}

/**
 * Флаги функций, которые читаются при сборке (import.meta.env), а не при запуске:
 * Vite подставляет значения в бандл, поэтому образу их задают через --build-arg
 * (frontend-redesign/Dockerfile, build-and-push.sh).
 *
 * AWS временно выключен решением владельца 26.10.2026. Для остальных флагов
 * отсутствующая или пустая переменная означает "включено"; регистр и крайние ASCII-пробелы не важны.
 * Иное значение — ошибка конфигурации сборки.
 */
export function buildFlagDefaults(): Record<'aiCopilot' | 'sso' | 'awsIntegration', boolean> {
	const parseFlag = (name: string, value: string | undefined, fallback = true) => {
		const normalized =
			value?.replace(/^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g, '').toLowerCase() || String(fallback);
		if (normalized !== 'true' && normalized !== 'false') {
			throw new Error(`${name} must be true or false`);
		}
		return normalized === 'true';
	};
	return {
		aiCopilot: parseFlag('VITE_FLAG_AI_COPILOT', import.meta.env.VITE_FLAG_AI_COPILOT),
		awsIntegration: parseFlag(
			'VITE_FLAG_AWS_INTEGRATION',
			import.meta.env.VITE_FLAG_AWS_INTEGRATION,
			false,
		),
		sso: parseFlag('VITE_FLAG_SSO', import.meta.env.VITE_FLAG_SSO),
	};
}

/** Флаг сборки: значение известно с загрузки приложения */
export type BuildFlag = keyof ReturnType<typeof buildFlagDefaults>;

/** Флаг сервера (/v1/features): значения нет, пока сервер не ответил */
export type ServerFlag = keyof Features;

export type FeatureFlag = BuildFlag | ServerFlag;

export const useFeaturesStore = create<FeaturesState>((set, get) => ({
	flags: buildFlagDefaults(),
	isEnabled: (flag) => get().flags[flag] ?? false,
	setFlag: (flag, value) =>
		set((state) => ({
			flags: { ...state.flags, [flag]: value },
		})),
}));
